import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Alert, Button, Card, Flex, Form, Input, Spin, Tabs, Typography,
} from 'antd';
import {
    useEffect, useMemo,
} from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import {
    settingsMutations, settingsQueries,
} from '@/api';
import {
    TextEditor,
} from '@/components';
import {
    AppRoute,
    SETTING_KEY_DEFAULT_INSTRUCTIONS,
    SETTINGS_DISPLAY_NAME_BY_KEY,
} from '@/constants';
import { SettingInstructionItemPayload } from '@/types';

type FormValues = {
    name: string;
    content: SettingInstructionItemPayload[];
};

const SETTING_KEY = SETTING_KEY_DEFAULT_INSTRUCTIONS;

function normalizeContent(raw: unknown): SettingInstructionItemPayload[] {
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw.map((item) => {
        if (
            item
            && typeof item === 'object'
            && 'instruction_name' in item
            && 'instruction_value' in item
        ) {
            const row = item as SettingInstructionItemPayload;
            return {
                instruction_name: String(row.instruction_name ?? ''),
                instruction_value: String(row.instruction_value ?? ''),
            };
        }
        return {
            instruction_name: '',
            instruction_value: '',
        };
    });
}

export const SettingsPage = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm<FormValues>();

    const { data: availableKeys, isSuccess: keysReady, isFetching: keysFetching } = useQuery(settingsQueries.availableKeys());

    const meta = useMemo(
        () => availableKeys?.find((k) => k.key === SETTING_KEY),
        [availableKeys],
    );
    const isKnownKey = Boolean(meta);
    const created = meta?.created ?? false;

    const { data: setting, isFetching: settingLoading } = useQuery({
        ...settingsQueries.getOneSetting(SETTING_KEY),
        enabled: keysReady && isKnownKey && created,
    });

    useEffect(() => {
        if (!keysReady || !isKnownKey) {
            return;
        }
        if (created && setting) {
            form.setFieldsValue({
                name: setting.name,
                content: normalizeContent(setting.content),
            });
        }
        if (!created) {
            form.setFieldsValue({
                name: SETTINGS_DISPLAY_NAME_BY_KEY[SETTING_KEY] ?? meta?.name ?? '',
                content: [],
            });
        }
    }, [keysReady, isKnownKey, created, setting, form, meta?.name]);

    const createMutation = useMutation(settingsMutations.create());
    const updateMutation = useMutation(settingsMutations.update(SETTING_KEY));

    const handleFinish = (values: FormValues) => {
        const payloadContent = values.content?.length ? values.content : [];
        if (!created) {
            createMutation.mutate({
                key: SETTING_KEY,
                name: values.name,
                content: payloadContent,
            });
        } else {
            updateMutation.mutate({
                name: values.name,
                content: payloadContent,
            });
        }
    };

    const saving = createMutation.isPending || updateMutation.isPending;
    const settingSyncing = Boolean(created && settingLoading);
    const pageBootstrapping = keysFetching && !keysReady;
    const formDisabled = saving || pageBootstrapping || settingSyncing;

    if (keysReady && !isKnownKey) {
        return (
            <>
                <Helmet>
                    <title>Налаштування</title>
                </Helmet>
                <Alert
                    type="error"
                    message="Ключ налаштувань недоступний"
                    showIcon
                />
                <Button
                    onClick={() => navigate(AppRoute.Projects)}
                    style={{ marginTop: 16 }}
                >
                    На головну
                </Button>
            </>
        );
    }

    const tabItems = [
        {
            key: 'default_instructions',
            label: 'Стандартні інструкції',
            children: (
                <Flex vertical gap="middle">
                    <Form.Item
                        name="name"
                        label="Назва"
                        rules={[{
                            required: true,
                            message: 'Вкажіть назву',
                        }]}
                        style={{ marginBottom: 0 }}
                    >
                        <Input
                            disabled={formDisabled}
                            maxLength={255}
                        />
                    </Form.Item>
                    <Spin spinning={pageBootstrapping || settingSyncing}>
                        <Form.List name="content">
                            {(fields, { add, remove }) => (
                                <>
                                    {fields.map(({
                                        key: fieldKey, name, ...restField
                                    }) => (
                                        <Card
                                            key={fieldKey}
                                            size="small"
                                            style={{ marginBottom: 12 }}
                                        >
                                            <Flex gap="small" vertical>
                                                <Flex justify="space-between" align="center">
                                                    <Typography.Text strong>
                                                        Інструкція
                                                    </Typography.Text>
                                                    <Button
                                                        type="link"
                                                        danger
                                                        onClick={() => remove(name)}
                                                        disabled={formDisabled}
                                                    >
                                                        Видалити
                                                    </Button>
                                                </Flex>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'instruction_name']}
                                                    label="Назва інструкції"
                                                    rules={[{
                                                        required: true,
                                                        message: 'Вкажіть назву',
                                                    }]}
                                                    style={{ marginBottom: 12 }}
                                                >
                                                    <Input
                                                        placeholder="Назва інструкції"
                                                        disabled={formDisabled}
                                                    />
                                                </Form.Item>
                                                <Form.Item
                                                    {...restField}
                                                    name={[name, 'instruction_value']}
                                                    label="Значення"
                                                    rules={[{
                                                        required: true,
                                                        message: 'Вкажіть значення',
                                                    }]}
                                                    style={{ marginBottom: 0 }}
                                                >
                                                    <TextEditor
                                                        disabled={formDisabled}
                                                        editorMinHeightPx={220}
                                                    />
                                                </Form.Item>
                                                {/*<TextEditorWarning />*/}
                                            </Flex>
                                        </Card>
                                    ))}
                                    <Button
                                        type="dashed"
                                        onClick={() => add({
                                            instruction_name: '',
                                            instruction_value: '',
                                        })}
                                        block
                                        disabled={formDisabled}
                                    >
                                        Додати інструкцію
                                    </Button>
                                </>
                            )}
                        </Form.List>
                    </Spin>
                </Flex>
            ),
        },
    ];

    return (
        <>
            <Helmet>
                <title>Налаштування</title>
            </Helmet>
            <Flex vertical gap="large">
                <Typography.Title
                    level={3}
                    style={{ margin: 0 }}
                >
                    Налаштування
                </Typography.Title>
                <Card>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleFinish}
                    >
                        <Tabs
                            type="line"
                            size="large"
                            items={tabItems}
                        />
                        <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={saving}
                                disabled={formDisabled}
                            >
                                Зберегти
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </Flex>
        </>
    );
};
